import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Row,
  Menu,
  Input,
  Button,
  Column,
  Select,
  Breadcrumb,
  Notification,
} from "@cea/ui-kit";
import { useApolloClient, useMutation } from "@apollo/client";
import { debounce } from "lodash-es";

import {
  FETCH_ESGT_FOLDERS,
  RENAME_ESGT_FOLDER,
  RENAME_ESGT_FILE,
  FETCH_ESGT_FILES,
} from "../../../queries/esgServices";
import AuthContext from "../../../contexts/AuthContext";
import { getNotification, getOrderByObject, sortOptions } from "../../../utils";
import OrganizationContext from "../../../contexts/OrganizationContext";
import { fetchProjectDetails } from "../../../utils/fetchProjectDetails";
import ReportsListView from "../../../components/ReportsListView/ReportsListView";
import ESGTReportUploadModal from "../ESGTReportUploadModal";
import ESGTCreateFolderModal from "../ESGTCreateFolderModal";
import useFileFolderDeleteDialog from "../useFileFolderDeleteDialog";

import styles from "./reports-list.module.css";

const serviceLinesOptions = [
  { label: "Inline Production Monitoring", value: "ipm" },
  { label: "Pre-Shipment Inspection", value: "psi" },
  { label: "Production Tracking", value: "productionTracking" },
  { label: "Factory Audit", value: "fa" },
];

const ESGTReportsList = () => {
  const [loading, setLoading] = useState(false);
  const [folderData, setFolderData] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingProject, setLoadingProject] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isCreateFileModalVisible, setIsCreateFileModalVisible] =
    useState(false);
  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] =
    useState(false);

  const apolloClient = useApolloClient();
  const { organizationId, projectId } = useParams();
  const {
    service,
    serviceMeta,
    organization,
    fetchOrganizationDetails,
    fetchServiceDetails,
    updateServiceMeta,
  } = useContext(OrganizationContext);
  const { user } = useContext(AuthContext);
  const [queryParams, setQueryParams] = useSearchParams();

  const [renameESGTFolder, { loading: folderRenameLoading }] =
    useMutation(RENAME_ESGT_FOLDER);
  const [renameESGTFile, { loading: fileRenameLoading }] =
    useMutation(RENAME_ESGT_FILE);

  const orderBy = queryParams.get("sort") || "createDate:desc";
  const folderId = queryParams.get("folderId") || null;
  const isRenameFolderLoading =
    folderRenameLoading || fileRenameLoading ? "loading_rename" : null;

  const fetchProject = async () => {
    setLoadingProject("loading_data");

    try {
      const { qaProject } = await fetchProjectDetails({
        apolloClient,
        projectId,
      });

      updateServiceMeta(qaProject);
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    } finally {
      setLoadingProject(null);
    }
  };

  const fetchFolders = async () => {
    if (serviceMeta) {
      try {
        setLoading("loading_data");

        const sortObject = getOrderByObject(orderBy);

        const { data } = await apolloClient.query({
          query: FETCH_ESGT_FOLDERS,
          variables: {
            where: {
              projectId: +projectId,
              id: folderId,
              organizationId,
            },
            orderBy: sortObject,
          },
          fetchPolicy: "network-only",
        });

        setFolderData(data.getESGTFolders);
      } catch (error) {
        const message = getNotification(error);

        Notification.error({
          message,
        });
      } finally {
        setLoading(null);
      }
    }
  };

  const fetchFiles = async (variables = {}) => {
    try {
      setLoading("loading_data");

      const sortObject = getOrderByObject(orderBy);

      const { data } = await apolloClient.query({
        query: FETCH_ESGT_FILES,
        variables: {
          ...variables,
          projectId,
          organizationId,
          orderBy: sortObject,
        },
      });

      setFolderData({
        folders: [],
        files: data.getESGTFiles,
      });
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => {
    if (!organization) {
      fetchOrganizationDetails(organizationId);
    }

    if (!service) {
      fetchServiceDetails({ serviceType: "esgt" });
    }

    if (!serviceMeta) {
      fetchProject();
    }

    if (inputValue.length) {
      fetchFiles({ name: inputValue });
    } else {
      fetchFolders(folderId);
    }
  }, [organization, service, serviceMeta, folderId, orderBy]);

  const onFolderCreate = (newFolder, parentFolder) => {
    if (!folderData?.id) {
      setFolderData({
        ...parentFolder,
        folders: [
          {
            projectId: +projectId,
            ...newFolder,
            __typename: "ESGTFolder",
            createdBy: user.name,
          },
        ],
        files: [],
        parentId: null,
        id: parentFolder.parentId,
        name: parentFolder.parentName,
        projectId: +projectId,
      });
    } else {
      setFolderData((prevState) => ({
        ...prevState,
        folders: [
          ...prevState.folders,
          {
            projectId: +projectId,
            ...newFolder,
            __typename: "ESGTFolder",
            createdBy: user.name,
          },
        ],
      }));
    }

    setIsCreateFolderModalVisible(false);
  };

  const onFileCreate = (newFile, parentFolder) => {
    setFolderData((prevState) => ({
      ...prevState,
      files: [
        ...prevState?.files,
        {
          ...newFile,
          __typename: "ESGTFile",
          projectId: +projectId,
          createdBy: user.name,
        },
      ],
    }));

    setIsCreateFileModalVisible(false);
  };

  const onFileUpdate = (newFile, parentFolder) => {
    if (!folderData?.id) {
      setFolderData((prevState) => ({
        ...parentFolder,
        files: prevState.files.map((file) => {
          if (file.tempId === newFile.tempId) {
            return {
              ...file,
              uploading: false,
              ...newFile,
            };
          }

          return file;
        }),
        folders: [],
        parentId: null,
        id: parentFolder.parentId,
        name: parentFolder.parentName,
        projectId: +projectId,
      }));
    } else {
      setFolderData((prevState) => ({
        ...prevState,
        files: prevState.files.map((file) => {
          if (file.tempId === newFile.tempId) {
            return {
              ...file,
              uploading: false,
              ...newFile,
            };
          }

          return file;
        }),
      }));
    }
  };

  const showCreateFolderModal = () => {
    setIsCreateFolderModalVisible(true);
  };

  const handleChange = (values) => {
    setSelectedServices(values);

    queryParams.set("serviceLine", values.join(","));
    setQueryParams(queryParams);
  };

  const onFolderOrFileRename = async (details, newName) => {
    try {
      if (details.__typename === "ESGTFolder") {
        await renameESGTFolder({
          variables: {
            data: {
              id: details.id,
              name: newName,
              projectId: +details.projectId,
            },
          },
        });

        setFolderData((prevState) => ({
          ...prevState,
          folders: prevState.folders.map((item) => {
            if (item.id === details.id) {
              return {
                ...item,
                name: newName,
              };
            }

            return item;
          }),
        }));
      } else {
        await renameESGTFile({
          variables: {
            data: {
              id: details.id,
              name: newName,
              projectId: +details.projectId,
            },
          },
        });

        setFolderData((prevState) => ({
          ...prevState,
          files: prevState.files.map((item) => {
            if (item.id === details.id) {
              return {
                ...item,
                name: newName,
              };
            }

            return item;
          }),
        }));
      }
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    }
  };

  const debouncer = useCallback(
    debounce((searchValue) => fetchFiles(searchValue), 750),
    []
  );

  const onNameSearch = (event) => {
    const searchValue = event.target.value;

    setInputValue(searchValue);

    if (searchValue.length) {
      debouncer({ name: searchValue });
    } else {
      fetchFolders();
    }
  };

  const breadcrumbMenu = () => (
    <Menu>
      <Menu.Item>
        <Link
          to={`/organizations/${organization?.id}/services/esgt/projects/${serviceMeta?.PID}`}
        >
          {serviceMeta?.Name}
        </Link>
      </Menu.Item>
    </Menu>
  );

  const handleOrderChange = (value = "") => {
    setSelectedOrder(value || null);

    queryParams.set("sort", value);
    setQueryParams(queryParams);
  };

  const header = (
    <div className={styles["reports-filter-container"]}>
      <Row gutter={[16, 32]}>
        <Column span={6}>
          <Input
            placeholder="Search all Files"
            value={inputValue}
            onChange={onNameSearch}
          />
        </Column>
        <Column span={12}>
          <Select
            onChange={handleChange}
            placeholder="Select Service"
            mode="multiple"
            value={selectedServices}
            options={serviceLinesOptions}
            className="min-w-[35%]"
          />
        </Column>
        <Column span={6}>
          <Select
            allowClear
            onChange={handleOrderChange}
            placeholder="Sort by"
            value={selectedOrder}
            options={sortOptions}
            className={styles["search-input"]}
          />
        </Column>
      </Row>
    </div>
  );

  const navHolder = (
    <div className={styles["flex-container"]}>
      <div className="breadcrumb-holder">
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to={`/organizations/${organization?.id}`}>
              {organization?.name}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/services">Services</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item overlay={breadcrumbMenu}>
            <Link
              to={`/organizations/${organization?.id}/services/${service?.id}`}
            >
              {service?.name}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{serviceMeta?.Name}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className={styles["actions-container"]}>
        <Button
          size="small"
          type="primary"
          className="theme-button"
          onClick={() => {
            setIsCreateFileModalVisible(true);
          }}
        >
          Upload File
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={showCreateFolderModal}
          className="theme-button"
        >
          Create Folder
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <ReportsListView
        header={header}
        navHolder={navHolder}
        loading={loading || loadingProject || isRenameFolderLoading}
        folderData={folderData}
        setFolderData={setFolderData}
        onRename={onFolderOrFileRename}
        useFileFolderDeleteDialog={useFileFolderDeleteDialog}
      />
      <ESGTCreateFolderModal
        projectId={projectId}
        isVisible={isCreateFolderModalVisible}
        handleCancel={() => {
          setIsCreateFolderModalVisible(false);
        }}
        parentId={folderId || folderData?.id}
        handleOk={onFolderCreate}
      />
      <ESGTReportUploadModal
        isVisible={isCreateFileModalVisible}
        onFileUpdate={onFileUpdate}
        onFileCreate={onFileCreate}
        handleCancel={() => {
          setIsCreateFileModalVisible(false);
        }}
        parentId={folderId || folderData?.id}
      />
    </>
  );
};

export default ESGTReportsList;
